'use client';

import { Accordion, Box, Container, Text, Title } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';

interface CoverageItem {
  title: string;
  description: string;
}

interface CoverageAccordionBlockProps {
  title: string;
  items: CoverageItem[];
}

export function CoverageAccordionBlock({ title, items }: CoverageAccordionBlockProps) {
  const safeItems = (items || [])
    .map((item) => {
      const rawTitle = typeof item.title === 'string' ? item.title : '';
      const rawDescription = typeof item.description === 'string' ? item.description : '';
      return {
        title: rawTitle.trim(),
        description: rawDescription.trim(),
      };
    })
    .filter((item) => item.title || item.description);

  if (!safeItems.length) return null;

  return (
    <Container size="xl" py={{ base: 50, md: 80 }}>
      <Title
        order={2}
        style={{
          color: 'var(--text-primary)',
          fontWeight: 500,
          fontSize: 'clamp(2rem, 3.8vw, 2.5rem)',
          lineHeight: 1.3,
          maxWidth: 760,
          marginBottom: 26,
        }}
      >
        {title}
      </Title>

      <Accordion
        variant="separated"
        multiple
        chevronPosition="right"
        defaultValue={[safeItems[0] ? 'item-0' : '']}
        styles={{
          item: {
            border: '1px solid var(--surface-border)',
            borderRadius: 999,
            background: 'var(--surface-muted)',
            overflow: 'hidden',
          },
          control: {
            minHeight: 92,
            paddingInline: 24,
            color: 'var(--text-primary)',
            fontWeight: 700,
            fontSize: 'clamp(1.12rem, 1.9vw, 1.45rem)',
            letterSpacing: '-0.01em',
            lineHeight: 1.4,
          },
          content: {
            paddingInline: 24,
            paddingBottom: 24,
          },
          chevron: {
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: 'var(--brand-primary)',
            color: '#fff',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
        chevron={<IconPlus size={20} />}
      >
        {safeItems.map((item, index) => {
          const label = item.title || item.description.split('\n')[0].slice(0, 80) || `Item ${index + 1}`;
          return (
            <Accordion.Item key={`item-${index}`} value={`item-${index}`}>
              <Accordion.Control icon={null}>{label}</Accordion.Control>
              <Accordion.Panel>
                <Box pb={4}>
                  <Text size="md" style={{ color: 'var(--text-secondary)', lineHeight: 1.75 }}>
                    {item.description}
                  </Text>
                </Box>
              </Accordion.Panel>
            </Accordion.Item>
          );
        })}
      </Accordion>
    </Container>
  );
}
